/**
 * Study Mode FAQ component
 */

import {Container} from '../ui/Container';
import {Section} from '../ui/Section';
import {AccordionItem} from '../ui/Accordion';
import {useScrollAnimation} from '../../hooks/useScrollAnimation';

interface FAQItem {
  question: string;
  answer: string;
}

const faqs: FAQItem[] = [
  {
    question: 'How does Study Mode mark my answers?',
    answer: 'Study Mode uses advanced AI to analyze your answers against the uploaded content and curriculum standards. It provides detailed feedback with inline annotations, step-by-step corrections, and explanations tailored to your specific mistakes.',
  },
  {
    question: 'What if I don\'t have the markscheme?',
    answer: 'No problem! Study Mode can generate accurate markschemes and model answers instantly. Just upload your questions, and our AI will create a comprehensive marking guide for you.',
  },
  {
    question: 'Does it work for essays and long-form answers?',
    answer: 'Yes! Study Mode supports all types of answers, including essays. You can add your rubric for maximum accuracy and get criterion-by-criterion scores with detailed annotations.',
  },
  {
    question: 'What subjects does Study Mode support?',
    answer: 'Study Mode works with any subject that involves written answers. It\'s particularly effective for STEM subjects, humanities, languages, and social sciences. The AI adapts to different marking styles and requirements.',
  },
  {
    question: 'How accurate is the marking?',
    answer: 'Our AI marking is designed to match expert-level accuracy. It considers context, partial credit, and various answer approaches. However, we always recommend reviewing AI feedback and consulting with teachers for important assessments.',
  },
  {
    question: 'Is it really instant?',
    answer: 'Yes! Most documents are analyzed and marked within seconds. Even complex essays with detailed rubrics are processed in under a minute. You\'ll see real-time progress as the AI works through your content.',
  },
];

export function StudyFAQ() {
  const {ref, isVisible} = useScrollAnimation();

  return (
    <Section id="faq" padding="lg" background="gray" ref={ref} className="bg-backgroundSecondary dark:bg-[#252526]">
      <Container>
        <div className={`max-w-3xl mx-auto transition-all duration-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          {/* Header */}
          <div className="text-center mb-12">
            <p className="text-sm font-medium text-orange-600 dark:text-orange-500 mb-3 uppercase tracking-wide">
              FAQ
            </p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-textPrimary dark:text-[#CCCCCC] mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-textSecondary dark:text-[#858585]">
              Everything you need to know about Study Mode
            </p>
          </div>

          {/* FAQ Items */}
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem
                key={index}
                question={faq.question}
                answer={faq.answer}
                defaultOpen={index === 0}
              />
            ))}
          </div>
        </div>
      </Container>
    </Section>
  );
}

